import React, { useEffect, useRef, useState } from "react"
import Dropdown from "../Dropdown/Dropdown"
import { getSmsReq, getTemplateReq, getTemplatesReq, sendSmsReq } from "../../features/emails/emails"
import { showSnackBar } from "../Snackbar/Snackbar"
import { useDispatch, useSelector } from "react-redux"
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider"
import noAvatar from "../../assets/images/icons/no_avatar.jpg"
import { extractTime, getSlashDateFormat, now, serverToLocal } from "../../utils/date"
import { phoneNumber } from "../../utils/payment"
import { NoData } from "../Icons/Icons"
import DebounceInput from "../DebouncedSearch/DebouncedSearch"
import { LuSend } from "react-icons/lu"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { MESSAGES_DELIVERY_STATUS } from "../../config/featureFlagKeys"
import { REQUEST_ERRORS_SMS } from "../../constants/requestErrorFlags"
import DeliveryStatusBadge from "../DeliveryStatusBadge/DeliveryStatusBadge"
import { isPendingDelivery } from "../DeliveryStatusBadge/deliveryStatus"
import { usePendingDeliveryPoll } from "../../hooks/usePendingDeliveryPoll"

const sameLetters = (current, next) => JSON.stringify(current) === JSON.stringify(next)

const hasPendingText = (letters) => Boolean(letters?.some((oneSms) => isPendingDelivery('sms', oneSms?.delivery?.status, oneSms?.createdAt)))

const SmsTab = ({ userId, userData }) => {
  const dispatch = useDispatch()
  const deliveryOn = useSelector(selectFeatureFlag(MESSAGES_DELIVERY_STATUS))
  const smsStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_SMS))
  const textareaRef = useRef(null)
  const { triggerLoading } = useMyContextLoading()
  const adminObjectString = localStorage.getItem("user")
  const adminObject = JSON.parse(adminObjectString)

  const [dropdownTemplates, setDropdownTemplates] = useState('')
  const [templates, setTemplates] = useState([])
  const [sms, setSms] = useState([])
  const smsRef = useRef(sms)
  const latestRequest = useRef(0)
  const [textAreaTemplate, setTextAreaTemplate] = useState('')
  const [message, setMessage] = useState('')
  const [search, setSearch] = useState('')

  const getTemplates = async () => {
    triggerLoading(true)
    const response = await dispatch(getTemplatesReq({ type: "sms" }))
    if (response?.payload?.data && response?.payload?.data?.length > 0) {
      setTemplates(response?.payload?.data)
    } else {
      console.log('No payment data available.')
    }
    if (response.error) {
      showSnackBar("error", "Something went wrong!")
    } else if (response) {
    }
    triggerLoading(false)
  }

  const getTemplate = async ({ templateId }) => {
    triggerLoading(true)
    const response = await dispatch(getTemplateReq({ templateId, userId }))
    if (response?.payload?.data?.message && response?.payload?.data?.message?.length > 0) {
      setTextAreaTemplate(response?.payload?.data?.message)
    } else {
      console.log('No payment data available.')
    }
    if (response.error) {
      showSnackBar("error", "Something went wrong!")
    } else if (response) {
    }
    triggerLoading(false)
  }
  const handleDropdown = async (e) => {
    setDropdownTemplates(e)
    await getTemplate({ templateId: e })
  }

  const getSms = async ({ silent = false } = {}) => {
    if (!silent) triggerLoading(true)

    latestRequest.current += 1
    const requestId = latestRequest.current

    const response = await dispatch(getSmsReq({ searchQuery: search, userId }))
    if (silent && (requestId !== latestRequest.current || response?.error || !Array.isArray(response?.payload?.data?.letters))) return

    if (response?.payload?.data) {
      const letters = response?.payload?.data?.letters
      setSms((current) => (silent && sameLetters(current, letters) ? current : letters))
    } else {
      console.log('No payment data available.')
    }
    if (response.error) {
      if (!silent) showSnackBar("error", "Something went wrong!")
    } else if (response) {
    }
    if (!silent) triggerLoading(false)
  }

  const sendSms = async () => {
    triggerLoading(true)
    const response = await dispatch(sendSmsReq({ message, userId }))
    if (!(smsStrict && response.error)) setMessage('')
    if (response.error) {
      await getSms()
      showSnackBar("error", smsStrict && typeof response.payload === 'string' ? response.payload : "Something went wrong!")
    } else {
      await getSms()
      showSnackBar("success", "SMS successfully sent")
      setDropdownTemplates('')
      setTextAreaTemplate('')
    }
    triggerLoading(false)
  }

  const handleChange = (value) => {
    setMessage(value)

    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    }
  }

  const hasPendingDelivery = deliveryOn && hasPendingText(sms)

  usePendingDeliveryPoll({
    enabled: hasPendingDelivery,
    onPoll: () => {
      if (hasPendingText(smsRef.current)) getSms({ silent: true })
    },
  })

  useEffect(() => {
    smsRef.current = sms
  }, [sms])

  useEffect(() => {
    const fetch = async () => {
      await getTemplates()
      await getSms()
    }
    fetch()
  }, [search])

  useEffect(() => {
    setMessage('')
    handleChange(textAreaTemplate)
  }, [textAreaTemplate])

  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    }
  }, [message])


  return (
    <div className="w-full mb-6">
      <Dropdown menuItems={templates} setValue={(e) => handleDropdown(e)}
        value={dropdownTemplates} variant="standard" label="SMS Template" />
      {/* Compose context — the number a NEW message is sent from. Labelled explicitly because
          the message history below carries its own per-message sender attribution; without
          this label the static From/To reads as if it applied to every message in the list. */}
      <div className="text-xs font-semibold uppercase tracking-wide text-text-secondary mt-6 mb-2 px-4">New message</div>
      <div className="flex mb-8 justify-between">
        <div className="flex flex-1 items-center gap-10 px-4">
          <div className="text-text-secondary">From</div>
          <div className="flex flex-row">
            <img src={noAvatar} alt="" className=" w-[40px] rounded-full md:mr-4" />
            <div className="hidden md:flex flex-col text-md">
              <span className="text-text-primary font-semibold">{adminObject?.email}</span>
              <span className="text-text-secondary">{phoneNumber(adminObject?.primaryPhone)}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-1 items-center gap-10 px-4 py-2">
          <div className="text-text-secondary">To</div>
          <div className="flex flex-row">
            <img src={noAvatar} alt="" className=" w-[40px] rounded-full md:mr-4" />
            <div className="hidden md:flex flex-col text-md">
              <span className="text-text-primary font-semibold">{userData?.email}</span>
              <span className="text-text-secondary">{phoneNumber(userData?.primaryPhone)}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="border border-divider rounded-2xl shadow-sm flex flex-col gap-2 bg-paper">
        <div className="px-4 py-5 flex border-b border-divider">
          <DebounceInput searchValue={search} setSearchValue={setSearch} placeholder="Search" />
        </div>
        <div
          className={`min-h-[400px] max-h-[600px] flex ${sms?.length > 0 ? "flex-col-reverse items-end" : "justify-center items-center"} gap-6 overflow-auto `}
          ref={(el) => {
            el && el.scrollTo(0, el.scrollHeight)
          }}>
          {sms?.length > 0 ? sms?.map((oneSms) => {
            const currentDate = now()
            const smsDate = serverToLocal(oneSms.createdAt)
            // Attribute each message so an automated reminder / another rep's text isn't
            // shown as if the viewing rep sent it. Automated wins the label; otherwise the
            // resolved sender name; falling back to a neutral "Team member".
            const senderLabel = oneSms?.automated
              ? 'Automated'
              : (oneSms?.senderName || 'Team member')
            return (
              <div className="flex items-center gap-4" key={oneSms?._id}>
                <div className="max-w-[1200px] ml-4 flex flex-col items-end gap-2 ">
                  <div className="flex items-center gap-2 text-text-secondary text-[12px]">
                    <span className={`font-medium ${oneSms?.automated ? 'text-warning-600' : 'text-text-primary'}`}>
                      {senderLabel}
                    </span>
                    {oneSms && (
                      <span>
                        {
                          currentDate.diff(smsDate) > 43200000 ?
                            getSlashDateFormat(oneSms.createdAt) :
                            extractTime(oneSms.createdAt)
                        }
                      </span>
                    )}
                    {deliveryOn && <DeliveryStatusBadge delivery={oneSms?.delivery} channel="sms" />}
                  </div>
                  <div className="text-text-primary text-[14px] p-3 bg-primary-main/10 rounded-lg">
                    {oneSms?.message}
                  </div>
                </div>
                <img src={noAvatar} alt="" className=" w-8 h-8 rounded-full md:mr-4" />
              </div>
            )
          }) :
            <div className="flex flex-col items-center justify-center">
              <NoData />
              No SMS
            </div>}
        </div>
        <div className="p-4 flex border-t border-divider">
          <textarea
            ref={textareaRef}
            className="w-full min-h-9 h-full bg-transparent text-text-primary placeholder:text-text-secondary focus:outline-none focus:border-transparent"
            placeholder="Type a message"
            onChange={(e) => handleChange(e.target.value)}
            value={message}
            style={{ resize: "none", }}
          />
          <button
            type="button"
            aria-label="Send message"
            className="flex items-center justify-center w-9 h-9 shrink-0 cursor-pointer text-text-secondary hover:text-primary-main transition-colors"
            onClick={async () => await sendSms()}>
            <LuSend size={18} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default SmsTab